import React, { useState } from "react";
import { X, Copy, Check, UserPlus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, getErrorMessage } from "@/lib/api";

const input = "bg-ivory border border-champagne rounded-md h-10 px-3 text-sm text-cocoa outline-none focus:border-mauve";

export function CopyLink({ link, expiresAt, testId = "activation-link" }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select the link and copy it manually"); }
  };
  return (
    <div className="bg-ivory border border-gold/40 rounded-md p-3 space-y-2" data-testid={`${testId}-box`}>
      <p className="text-[10px] uppercase tracking-widest text-cocoa/50 inline-flex items-center gap-1.5"><Link2 size={11} /> Set-password link · valid until {expiresAt ? new Date(expiresAt).toLocaleDateString() : "—"}</p>
      <div className="flex gap-2 items-center">
        <input readOnly value={link} onFocus={(e) => e.target.select()} data-testid={testId} className={`${input} flex-1 min-w-0 text-xs`} />
        <button type="button" onClick={copy} data-testid={`${testId}-copy`} className="ss-pill ss-pill-gold h-10 shrink-0">{copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}</button>
      </div>
      <p className="text-xs text-cocoa/60">Send this to the member yourself. The account becomes active the moment they set their password.</p>
    </div>
  );
}

export function NewAccountModal({ plans, planLabel, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("free");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/admin/users", { email: email.trim(), name: name.trim() || null, plan, note: note.trim() || null });
      setResult(r.data); onCreated();
      toast.success(`Account created for ${r.data.user.email}`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-account-modal">
      <div className="absolute inset-0 bg-cocoa/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg ss-card bg-ivory p-6 space-y-4 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Members</p>
            <h3 className="font-heading text-2xl text-cocoa">New account</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-champagne" data-testid="new-account-close" aria-label="Close"><X size={18} /></button>
        </div>
        {result ? (
          <div className="space-y-3" data-testid="new-account-result">
            <p className="text-sm text-cocoa/75"><strong>{result.user.email}</strong> is set up on <strong>{planLabel[result.user.plan] || result.user.plan}</strong> and waiting for a password.</p>
            <CopyLink link={result.link} expiresAt={result.expires_at} />
            <button onClick={onClose} className="ss-pill ss-pill-primary h-10 w-full" data-testid="new-account-done">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input data-testid="new-account-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={`${input} w-full`} />
            <input data-testid="new-account-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="First name or alias (optional)" className={`${input} w-full`} />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-cocoa/50">Initial plan</label>
              <select data-testid="new-account-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className={`${input} w-full mt-1`}>
                {plans.map((p) => <option key={p} value={p}>{planLabel[p]}</option>)}
              </select>
            </div>
            <input data-testid="new-account-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Internal note (optional)" className={`${input} w-full`} />
            <button type="submit" disabled={busy} className="ss-pill ss-pill-primary h-11 w-full disabled:opacity-50" data-testid="new-account-submit"><UserPlus size={14} /> {busy ? "Creating…" : "Create account & generate link"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
